import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { FormErrorBanner } from '../FormErrorBanner';
import { hiringLinksAPI, type HiringLink, type HiringRaiseCandidate, type HiringRaiseResult } from '../../services/api';
import { ManageBudgetsLink } from './ManageBudgetsLink';
import {
  RAISE_CHECK_AFTER_MS,
  RAISE_CODES,
  RAISE_TICKS,
  RAISE_WORDS,
  canCloseAsNotCreated,
  candidatesOf,
  formatHiringMoment,
  hiringCodeOf,
  isHiringOff,
  localiseRaiseMessage,
  raiseCandidateRow,
  raiseErrorText,
  raiseNeedsAPerson,
  raiseStandingSentence,
} from './hiringText';

/**
 * A request to raise hiring that Recruitment has not confirmed: where it stands, and
 * what can be done about it. Shown in the hiring panel for a link that is still being
 * raised, and in the Raise hiring window straight after a request.
 *
 * NOTHING HERE SENDS ANYTHING TO RECRUITMENT. "Check again" reads Recruitment's list;
 * "This one" and "close this request" only record, in ChannelPlex, how the request
 * ended. There is no "Raise again" here, on purpose: the request may have created an
 * opening, and a second request would make a second one.
 *
 * For someone who may manage hiring it checks again by itself a few times (5, 15 and 45
 * seconds after it is shown), then leaves a "Check again" button. Someone who may only
 * view sees the sentence and nothing to press.
 */

type Answer = Pick<HiringRaiseResult, 'outcome' | 'message' | 'candidates'>;

const SMALL_BUTTON = 'rounded-full border px-3 py-1 text-xs font-semibold transition-colors disabled:opacity-50';

export function RaiseRequestStatus({
  projectId, link, canManage, initial = null, onResult, onHiringGone,
}: {
  projectId: number;
  /** The link being raised, as its owner has it now. */
  link: HiringLink;
  canManage: boolean;
  /** The answer the request was last given, when its owner has one (the raise itself, just answered). */
  initial?: Answer | null;
  /** A check or a choice was answered: the request as the server has it now. */
  onResult: (result: HiringRaiseResult) => void;
  /** Hiring was switched off for the project while this was on show. */
  onHiringGone: () => void;
}) {
  const [answer, setAnswer] = useState<Answer | null>(initial);
  const [busy, setBusy] = useState<'check' | 'pick' | 'close' | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The checks ChannelPlex makes by itself are over: from then on the button is offered.
  const [autoDone, setAutoDone] = useState(false);
  const [confirmingClose, setConfirmingClose] = useState(false);
  // The server asked for "None of these is this request's opening" before it closes.
  const [askNone, setAskNone] = useState(false);
  const [noneOfThese, setNoneOfThese] = useState(false);
  // Redrawn once the time the request stays open has passed, so the close control appears.
  const [, setClock] = useState(0);

  const alive = useRef(true);
  // One call at a time, whatever asks for it: a timer, a click, a second click.
  const working = useRef(false);
  const latest = useRef({ answer, onResult, onHiringGone });
  latest.current = { answer, onResult, onHiringGone };

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const took = (result: HiringRaiseResult | undefined) => {
    if (!result) return;
    // The owner is told whether or not this is still on show.
    latest.current.onResult(result);
    if (alive.current) setAnswer({ outcome: result.outcome, message: result.message, candidates: result.candidates ?? [] });
  };

  const check = async (by: 'itself' | 'person') => {
    if (working.current) return;
    // A request that waits for a person is not changed by reading the list again.
    if (by === 'itself' && raiseNeedsAPerson(latest.current.answer?.outcome)) return;
    working.current = true;
    setBusy('check');
    if (by === 'person') setError(null);
    try {
      const res = await hiringLinksAPI.checkRaise(projectId, link.id);
      took(res?.data?.data);
    } catch (err) {
      if (isHiringOff(err)) latest.current.onHiringGone();
      // A check made by itself that fails says nothing: the next one, or the button, asks again.
      else if (alive.current && by === 'person') setError(raiseErrorText(err, RAISE_WORDS.checkFailed));
    } finally {
      working.current = false;
      if (alive.current) setBusy(null);
    }
  };
  const checkNow = useRef(check);
  checkNow.current = check;

  useEffect(() => {
    if (!canManage) return undefined;
    setAutoDone(false);
    const last = RAISE_CHECK_AFTER_MS.length - 1;
    const timers = RAISE_CHECK_AFTER_MS.map((ms, i) => setTimeout(() => {
      if (i === last) setAutoDone(true);
      void checkNow.current('itself');
    }, ms));
    return () => timers.forEach(clearTimeout);
  }, [canManage, projectId, link.id]);

  const settleAfter = link.raise?.settleAfter ?? null;
  useEffect(() => {
    const wait = settleAfter ? Date.parse(settleAfter) - Date.now() : NaN;
    if (!canManage || !Number.isFinite(wait) || wait <= 0) return undefined;
    const timer = setTimeout(() => setClock((n) => n + 1), Math.min(wait + 500, 2_000_000_000));
    return () => clearTimeout(timer);
  }, [canManage, settleAfter]);

  const settle = async (what: 'pick' | 'close', openingId?: number) => {
    if (working.current) return;
    working.current = true;
    setBusy(what);
    setError(null);
    try {
      const res = await hiringLinksAPI.settleRaise(
        projectId,
        link.id,
        what === 'pick' && openingId != null
          ? { result: 'created', openingId }
          : { result: 'not_created', ...(noneOfThese ? { noneOfThese: true } : {}) },
      );
      took(res?.data?.data);
      if (alive.current) setConfirmingClose(false);
    } catch (err) {
      if (isHiringOff(err)) {
        latest.current.onHiringGone();
        return;
      }
      if (!alive.current) return;
      setError(raiseErrorText(err, what === 'pick' ? RAISE_WORDS.pickFailed : RAISE_WORDS.closeFailed));
      // The refusal lists the new openings it means: they are shown, to pick from.
      const listed = candidatesOf(err);
      if (listed.length > 0) {
        setAnswer((prev) => ({
          outcome: prev?.outcome ?? 'needs_choice',
          message: prev?.message ?? '',
          candidates: listed,
        }));
      }
      if (what === 'close') {
        const message = String((err as { response?: { data?: { message?: unknown } } })?.response?.data?.message ?? '');
        // Offered only when the server's own sentence asks for it.
        if (hiringCodeOf(err) === RAISE_CODES.candidateExists && message.includes(RAISE_TICKS.noneOfThese)) setAskNone(true);
        else setConfirmingClose(false);
      }
    } finally {
      working.current = false;
      if (alive.current) setBusy(null);
    }
  };

  const message = localiseRaiseMessage(answer?.message) || raiseStandingSentence(link);
  const candidates: HiringRaiseCandidate[] = answer?.candidates ?? [];
  const needsPerson = raiseNeedsAPerson(answer?.outcome);
  const sentAt = link.raise?.sentAt ?? null;
  const closable = canManage && canCloseAsNotCreated(link, Date.now(), answer?.outcome);

  return (
    <div role="group" aria-label={RAISE_WORDS.standingLabel} className="space-y-2 rounded-lg border border-violet-200 bg-violet-50 px-3 py-3">
      <p className="text-sm text-gray-900">{message}</p>
      {sentAt && <p className="text-xs text-gray-600">{RAISE_WORDS.sentOn(formatHiringMoment(sentAt))}</p>}
      {error && <FormErrorBanner message={error} />}

      {candidates.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-semibold text-gray-700">{RAISE_WORDS.newOpenings}</p>
          <ul className="space-y-1">
            {candidates.map((candidate) => {
              const row = raiseCandidateRow(candidate);
              return (
                <li key={candidate.openingId} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {row.title}
                      {row.tag && (
                        <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">{row.tag}</span>
                      )}
                    </p>
                    <p className="text-xs text-gray-600">{row.line}</p>
                  </div>
                  {canManage && row.pickable && (
                    <button
                      type="button"
                      aria-label={RAISE_WORDS.thisOneLabel(candidate.openingId)}
                      onClick={() => void settle('pick', candidate.openingId)}
                      disabled={busy !== null}
                      className={`${SMALL_BUTTON} border-primary-200 text-primary-700 hover:bg-primary-50`}
                    >
                      {RAISE_WORDS.thisOne}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {canManage && (
        <div className="space-y-2">
          {/* Beside "Check again": the request says to look in 1Office before it is closed. With what to look for there. */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            {!autoDone && !needsPerson ? (
              <p className="text-xs text-gray-600">{RAISE_WORDS.checksByItself}</p>
            ) : (
              <button
                type="button"
                onClick={() => void check('person')}
                disabled={busy !== null}
                className={`${SMALL_BUTTON} inline-flex items-center gap-1 border-gray-300 bg-white text-gray-700 hover:bg-gray-50`}
              >
                {busy === 'check' && <Loader2 className="h-3 w-3 animate-spin" />}
                {busy === 'check' ? RAISE_WORDS.checking : RAISE_WORDS.checkAgain}
              </button>
            )}
            <ManageBudgetsLink projectId={projectId} budgetId={link.budget?.id} budgetName={link.budget?.name} openingId={link.openingId ?? link.raise?.answeredOpeningId} />
          </div>

          {/* Two clicks, as Stop following: the first only asks. */}
          {closable && (confirmingClose ? (
            <div role="group" aria-label={RAISE_WORDS.closeGroupLabel} className="space-y-2 border-t border-violet-200 pt-2">
              <p className="text-xs">
                <span className="font-medium text-gray-700">{RAISE_WORDS.closeQuestion}</span>{' '}
                <span className="text-gray-600">{RAISE_WORDS.closeWarning}</span>
              </p>
              {askNone && (
                <label className="flex items-start gap-2 text-xs text-gray-700">
                  <input type="checkbox" className="mt-0.5" checked={noneOfThese} onChange={(e) => setNoneOfThese(e.target.checked)} />
                  {RAISE_TICKS.noneOfThese}
                </label>
              )}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  autoFocus
                  onClick={() => setConfirmingClose(false)}
                  disabled={busy !== null}
                  className={`${SMALL_BUTTON} border-gray-300 bg-white text-gray-700 hover:bg-gray-50`}
                >
                  {RAISE_WORDS.closeNo}
                </button>
                <button
                  type="button"
                  onClick={() => void settle('close')}
                  disabled={busy !== null || (askNone && !noneOfThese)}
                  className={`${SMALL_BUTTON} inline-flex items-center gap-1 border-red-600 bg-red-600 text-white hover:bg-red-700`}
                >
                  {busy === 'close' && <Loader2 className="h-3 w-3 animate-spin" />}
                  {RAISE_WORDS.closeYes}
                </button>
              </div>
            </div>
          ) : (
            <div className="border-t border-violet-200 pt-2">
              <button
                type="button"
                onClick={() => {
                  setError(null);
                  setConfirmingClose(true);
                }}
                disabled={busy !== null}
                className={`${SMALL_BUTTON} border-red-200 bg-white text-red-600 hover:bg-red-50`}
              >
                {RAISE_WORDS.closeRequest}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default RaiseRequestStatus;
